// SiteUP CRM (2026-09-19): lógica pura do menu Compartilhar do cartão.
// Fica fora do componente para poder ser testada sem montar Vue: montar a URL
// canônica, montar o texto da mensagem do chat interno e decidir se o
// destinatário já abre o cartão.

// O chat interno não tem feature flag de conta: quem entra nele é quem tem a
// permissão da tela (as mesmas de `dashboard.routes.js`). Sem elas, a ação de
// enviar não aparece no menu.
export const INTERNAL_CHAT_PERMISSIONS = [
  'administrator',
  'agent',
  'team_chat_view',
];

/** Resultado de `resolveRecipientAccess`. */
export const RECIPIENT_ACCESS = {
  // Já abre o cartão hoje: envia direto.
  GRANTED: 'granted',
  // Não abre e quem envia administra o cartão: oferece dar acesso de leitura.
  NEEDS_ACCESS: 'needs_access',
  // Não abre e quem envia não administra: envia com aviso.
  UNKNOWN: 'unknown',
};

const positiveId = value => {
  const text = String(value ?? '').trim();
  return /^[1-9]\d*$/.test(text) ? Number(text) : null;
};

const asBoolean = value =>
  value === true || value === 'true' || value === 1 || value === '1';

/**
 * Caminho canônico do cartão no workspace V2, o mesmo que o backend devolve em
 * `share_url` e o único que `kanbanMessageLinks` reconhece.
 */
export const buildCardSharePath = ({ accountId, itemId }) => {
  const account = positiveId(accountId);
  const item = positiveId(itemId);
  if (account === null || item === null) return '';
  return `/app/accounts/${account}/v2/kanban?item=${item}`;
};

/**
 * URL absoluta do cartão, na mesma origem. Sem origem utilizável devolve ''
 * para o chamador não enviar um link quebrado.
 */
export const buildCardShareUrl = ({ accountId, itemId, origin }) => {
  const path = buildCardSharePath({ accountId, itemId });
  if (!path) return '';
  try {
    return new URL(path, origin).toString();
  } catch {
    return '';
  }
};

/**
 * Texto simples da mensagem: a linha opcional de quem envia, quebra de linha e
 * a URL do cartão. Sem URL não existe mensagem.
 */
export const buildCardShareMessage = ({ note = '', url = '' } = {}) => {
  const link = String(url || '').trim();
  if (!link) return '';
  const line = String(note || '')
    .trim()
    .replace(/\s+$/g, '');
  return line ? `${line}\n${link}` : link;
};

const assignedAgentIds = item =>
  (Array.isArray(item?.assigned_agents) ? item.assigned_agents : [])
    .map(agent => positiveId(agent?.id ?? agent))
    .filter(id => id !== null);

const funnelSupervisorIds = funnel =>
  (Array.isArray(funnel?.settings?.agents) ? funnel.settings.agents : [])
    .filter(
      agent =>
        agent && (asBoolean(agent.is_supervisor) || asBoolean(agent.supervisor))
    )
    .map(agent => positiveId(agent.id))
    .filter(id => id !== null);

/**
 * Espelha `Kanban::AccessResolver` com o que o front já tem em mãos:
 * administrador da conta, agente atribuído ao cartão, supervisor do funil e
 * acesso compartilhado. Quando nada disso responde, o caso depende de quem
 * envia poder ou não conceder acesso.
 */
export const resolveRecipientAccess = ({
  recipient,
  item,
  funnel,
  sharedAccessUserIds = [],
  senderCanManage = false,
} = {}) => {
  const recipientId = positiveId(recipient?.id);
  if (recipientId === null) return RECIPIENT_ACCESS.UNKNOWN;

  if (String(recipient?.role || '') === 'administrator') {
    return RECIPIENT_ACCESS.GRANTED;
  }
  if (assignedAgentIds(item).includes(recipientId)) {
    return RECIPIENT_ACCESS.GRANTED;
  }
  if (funnelSupervisorIds(funnel).includes(recipientId)) {
    return RECIPIENT_ACCESS.GRANTED;
  }
  const shared = (Array.isArray(sharedAccessUserIds) ? sharedAccessUserIds : [])
    .map(positiveId)
    .filter(id => id !== null);
  if (shared.includes(recipientId)) return RECIPIENT_ACCESS.GRANTED;

  return senderCanManage
    ? RECIPIENT_ACCESS.NEEDS_ACCESS
    : RECIPIENT_ACCESS.UNKNOWN;
};
